import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { batchDocumentService } from '../services/batchDocumentService'
import { PatientSearch } from '../components/PatientSearch'
import type { BatchDocument } from '../types/batchDocument'
import { formatDate } from '../utils/locale'
import './BatchDocuments.css'

export function BatchDocuments() {
  const queryClient = useQueryClient()
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null)
  const [linkFileId, setLinkFileId] = useState<string | null>(null)
  const [previewFileId, setPreviewFileId] = useState<string | null>(null)
  const [previewFileName, setPreviewFileName] = useState<string>('')

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['batch-documents', 'batches'],
    queryFn: () => batchDocumentService.listBatches(),
  })

  const { data: files = [], isLoading: filesLoading } = useQuery({
    queryKey: ['batch-documents', 'files', selectedBatchId],
    queryFn: () => batchDocumentService.listBatchFiles(selectedBatchId!),
    enabled: !!selectedBatchId,
  })

  const { data: previewData, isLoading: previewLoading } = useQuery({
    queryKey: ['batch-documents', 'preview', previewFileId],
    queryFn: () => batchDocumentService.getPreviewUrl(previewFileId!),
    enabled: !!previewFileId,
  })

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => batchDocumentService.uploadBatch(files),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['batch-documents'] })
      setSelectedBatchId(data.batchId)
    },
  })

  const extractMutation = useMutation({
    mutationFn: (fileId: string) => batchDocumentService.extractText(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-documents'] })
    },
  })

  const acceptMutation = useMutation({
    mutationFn: ({ fileId, patientId }: { fileId: string; patientId: string }) =>
      batchDocumentService.acceptMatch(fileId, patientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-documents'] })
    },
  })

  const linkMutation = useMutation({
    mutationFn: ({ fileId, patientId }: { fileId: string; patientId: string }) =>
      batchDocumentService.linkToPatient(fileId, patientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-documents'] })
      setLinkFileId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (fileId: string) => batchDocumentService.deleteFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-documents'] })
    },
  })

  const [isDragging, setIsDragging] = useState(false)

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    const accepted = Array.from(files).filter(
      (f) =>
        f.type.startsWith('image/') ||
        f.name.toLowerCase().endsWith('.pdf') ||
        f.name.toLowerCase().match(/\.(jpg|jpeg|png|tiff)$/i)
    )
    if (accepted.length > 0) uploadMutation.mutate(accepted)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files)
    e.target.value = ''
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const handleAccept = (doc: BatchDocument) => {
    if (doc.suggestedPatientId) {
      acceptMutation.mutate({ fileId: doc.fileId, patientId: doc.suggestedPatientId })
    }
  }

  const handleLinkSelect = (patientId: string) => {
    if (linkFileId) {
      linkMutation.mutate({ fileId: linkFileId, patientId })
    }
  }

  const statusLabel: Record<string, string> = {
    pending_ocr: 'Pending OCR',
    ocr_done: 'OCR done',
    suggested: 'Suggested match',
    unmatched: 'Unmatched',
    accepted: 'Accepted',
  }

  return (
    <div className="batch-documents" role="main">
      <h1>Batching</h1>
      <p className="batch-documents-intro">
        Upload documents in batch, run OCR to extract text, and match or link them to patients.
      </p>

      <section className="batch-upload-section" aria-labelledby="batch-upload-heading">
        <h2 id="batch-upload-heading">Upload batch</h2>
        <div
          className={`batch-upload-dropzone ${isDragging ? 'batch-upload-dropzone-active' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label="Drop files here or use Choose files to upload"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              const input = e.currentTarget.querySelector<HTMLInputElement>('input[type="file"]')
              input?.click()
            }
          }}
        >
          <label className="batch-upload-label">
            <span className="batch-upload-button">Choose files</span>
            <input
              type="file"
              multiple
              accept=".pdf,image/*,.jpg,.jpeg,.png,.tiff"
              onChange={handleFileSelect}
              disabled={uploadMutation.isPending}
              aria-label="Select files to upload"
            />
          </label>
          <span className="batch-upload-hint">or drag a batch of files here</span>
        </div>
        {uploadMutation.isPending && <p>Uploading…</p>}
        {uploadMutation.isError && (
          <p className="batch-error" role="alert">
            Upload failed: {(uploadMutation.error as Error).message}
          </p>
        )}
        {uploadMutation.isSuccess && (
          <p className="batch-success">
            Batch created with {uploadMutation.data?.files?.length ?? 0} file(s).
          </p>
        )}
      </section>

      <section className="batch-list-section" aria-labelledby="batch-list-heading">
        <h2 id="batch-list-heading">Batches</h2>
        {batchesLoading ? (
          <p>Loading batches…</p>
        ) : batches.length === 0 ? (
          <p>No batches yet. Upload files above.</p>
        ) : (
          <ul className="batch-list">
            {batches.map((b) => (
              <li key={b.batchId}>
                <button
                  type="button"
                  className={selectedBatchId === b.batchId ? 'active' : ''}
                  onClick={() => setSelectedBatchId(b.batchId)}
                >
                  Batch {b.batchId.slice(0, 8)}… — {b.fileCount} file(s) — {b.uploadedAt ? formatDate(b.uploadedAt) : '—'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selectedBatchId && (
        <section className="batch-files-section" aria-labelledby="batch-files-heading">
          <h2 id="batch-files-heading">Files in batch</h2>
          {filesLoading ? (
            <p>Loading files…</p>
          ) : files.length === 0 ? (
            <p>No files in this batch (or they have been matched and moved).</p>
          ) : (
            <div className="batch-files-table-wrap">
              <table className="batch-files-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Status</th>
                    <th>Suggested patient</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((doc) => (
                    <tr key={doc.fileId}>
                      <td>
                        <button
                          type="button"
                          className="batch-file-name-button"
                          onClick={() => {
                            setPreviewFileId(doc.fileId)
                            setPreviewFileName(doc.fileName)
                          }}
                        >
                          <span className="batch-file-name">{doc.fileName}</span>
                          <span className="batch-file-meta">
                            {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(1)} KB` : ''} · {doc.uploadedAt ? formatDate(doc.uploadedAt) : ''}
                          </span>
                        </button>
                      </td>
                      <td>{statusLabel[doc.status] ?? doc.status}</td>
                      <td>
                        {doc.suggestedPatientName ? (
                          <span>{doc.suggestedPatientName}</span>
                        ) : (
                          <span className="batch-muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="batch-file-actions">
                          {doc.status === 'pending_ocr' && (
                            <button
                              type="button"
                              onClick={() => extractMutation.mutate(doc.fileId)}
                              disabled={extractMutation.isPending}
                            >
                              Extract text
                            </button>
                          )}
                          {doc.status === 'suggested' && doc.suggestedPatientId && (
                            <button
                              type="button"
                              onClick={() => handleAccept(doc)}
                              disabled={acceptMutation.isPending}
                            >
                              Accept match
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setLinkFileId(doc.fileId)}
                          >
                            Search for patient
                          </button>
                          <button
                            type="button"
                            className="batch-file-action-remove"
                            onClick={() => deleteMutation.mutate(doc.fileId)}
                            disabled={deleteMutation.isPending}
                            aria-label={`Remove ${doc.fileName} from batch`}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {previewFileId && (
        <div
          className="batch-preview-modal"
          role="dialog"
          aria-labelledby="batch-preview-title"
          aria-modal="true"
          onClick={() => setPreviewFileId(null)}
        >
          <div
            className="batch-preview-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="batch-preview-modal-header">
              <h3 id="batch-preview-title">{previewFileName || 'Document preview'}</h3>
              <button
                type="button"
                className="batch-preview-close"
                onClick={() => setPreviewFileId(null)}
                aria-label="Close preview"
              >
                ×
              </button>
            </div>
            <div className="batch-preview-modal-body">
              {previewLoading && <p>Loading preview…</p>}
              {previewData?.url && (
                <iframe
                  title={previewFileName || 'Document preview'}
                  src={previewData.url}
                  className="batch-preview-iframe"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {linkFileId && (
        <div className="batch-link-modal" role="dialog" aria-labelledby="batch-link-title" aria-modal="true">
          <div className="batch-link-modal-card">
            <h3 id="batch-link-title">Link document to patient</h3>
            <PatientSearch
              onSelect={handleLinkSelect}
              placeholder="Search for patient to link…"
            />
            <button
              type="button"
              className="batch-link-cancel"
              onClick={() => setLinkFileId(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
